import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { Spinner, Alert, StatusBadge, EmptyState } from '../../components/UI';

function formatWhen(ts) {
  if (!ts) return '-';
  return new Date(ts).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [papers, setPapers] = useState(null);
  const [attempts, setAttempts] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get('/reports/summary'), api.get('/papers'), api.get('/results')])
      .then(([s, p, a]) => { setSummary(s); setPapers(p); setAttempts(a); })
      .catch(e => setError(e.message));
  }, []);

  if (!summary && !error) return <Spinner />;

  const livePaper = papers?.find(p => p.status === 'active');
  const { overview, pass } = summary || {};

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="eyebrow">Overview</div>
          <h1>Dashboard</h1>
        </div>
        <Link to="/admin/papers" className="btn btn-primary">Manage question papers</Link>
      </div>

      <Alert type="danger">{error}</Alert>

      {livePaper ? (
        <Alert type="success">
          <strong>{livePaper.title}</strong> is currently live &mdash; {livePaper.question_count} questions, {livePaper.attempt_count} attempts so far.
        </Alert>
      ) : (
        <Alert type="warning">No exam paper is live right now.</Alert>
      )}

      <div className="stat-grid">
        <div className="stat-card">
          <div className="label">Total attempts</div>
          <div className="value">{overview?.attempts ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="label">Pending AI review</div>
          <div className="value">{overview?.pending_review ?? 0}</div>
          <div className="sub"><Link to="/admin/results">Go to review queue &rarr;</Link></div>
        </div>
        <div className="stat-card">
          <div className="label">Average score</div>
          <div className="value">{overview?.avg_score ?? '-'}</div>
        </div>
        <div className="stat-card">
          <div className="label">Pass rate</div>
          <div className="value">{pass?.total ? `${Math.round((pass.passed / pass.total) * 100)}%` : '-'}</div>
          <div className="sub">{pass?.passed ?? 0} / {pass?.total ?? 0} evaluated attempts</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Recent exam attempts</h3>
          <Link to="/admin/results" className="btn btn-outline btn-sm">View all &rarr;</Link>
        </div>
        {!attempts && !error ? <Spinner /> : attempts && attempts.length === 0 ? (
          <EmptyState title="No attempts yet" sub="Attempts will appear here once candidates start taking exams." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Candidate</th><th>Paper</th><th>Status</th><th>Attempted</th>
                  <th>Score</th><th>Submitted</th><th></th>
                </tr>
              </thead>
              <tbody>
                {(attempts || []).slice(0, 6).map(a => {
                  const pct = a.max_score > 0 ? Math.round((a.total_score / a.max_score) * 100) : null;
                  return (
                    <tr
                      key={a.id}
                      className="row-clickable"
                      onClick={() => navigate(`/admin/results/${a.id}`)}
                    >
                      <td>{a.user_name}<div className="text-sm text-muted mono">{a.mobile}</div></td>
                      <td>{a.paper_title}</td>
                      <td>
                        <StatusBadge status={a.status} />
                        {a.pending_reviews > 0 && <div className="text-sm text-muted mt-2">{a.pending_reviews} pending AI review</div>}
                      </td>
                      <td className="mono">{a.total_questions != null ? `${a.answered} / ${a.total_questions}` : '-'}</td>
                      <td className="mono">
                        {a.total_score ?? '-'} / {a.max_score ?? '-'}
                        {pct !== null && <span className="text-muted"> ({pct}%)</span>}
                      </td>
                      <td className="text-sm text-muted">{formatWhen(a.submitted_at)}</td>
                      <td><Link to={`/admin/results/${a.id}`} className="btn btn-outline btn-sm" onClick={e => e.stopPropagation()}>View</Link></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
